import React, { useState, useEffect } from 'react';
import { Tooltip, Row, Col, Avatar, Button, Modal } from 'antd';
import { isEmpty } from 'lodash';
import { CreativeExpandedCard } from './CreativeExpandedCard';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
  TooltipText,
} from '../../../../../../shared/globalStyling/styledText';
import { formatWholeNumber } from '../../../../../../../core/utils/campaigns';
import uuid from 'uuid';
import SimpleTable from '../../../../../../shared/SimpleTable';
import { CloudOutlined, VideoCameraOutlined, Html5Outlined, LinkOutlined } from '@ant-design/icons';
import { Storage } from 'aws-amplify';

const VideoPreviewCell = ({ s3VideoKey, onClick }) => {
  const [videoUrl, setVideoUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let isMounted = true;

    const fetchUrl = async () => {
      if (!s3VideoKey) return;

      try {
        const url = await Storage.get(s3VideoKey, { expires: 600000 });
        if (isMounted) {
          setVideoUrl(url);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching video URL:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUrl();

    return () => {
      isMounted = false;
    };
  }, [s3VideoKey]);

  if (loading) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          marginLeft: '-16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
        }}
      >
        <VideoCameraOutlined style={{ fontSize: '20px', color: '#999' }} />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      style={{
        width: '50px',
        height: '50px',
        cursor: 'pointer',
        marginLeft: '-16px',
      }}
    >
      <video
        src={videoUrl}
        type="video/mp4"
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
        muted
        autoPlay
        loop
      />
    </div>
  );
};

const VideoModal = ({ s3VideoKey, visible, onClose, videoName }) => {
  const [videoUrl, setVideoUrl] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchUrl = async () => {
      if (!s3VideoKey) return;

      try {
        const url = await Storage.get(s3VideoKey, { expires: 600000 });
        if (isMounted) {
          setVideoUrl(url);
        }
      } catch (error) {
        console.error('Error fetching video URL for modal:', error);
      }
    };

    if (visible) {
      fetchUrl();
    }

    return () => {
      isMounted = false;
    };
  }, [s3VideoKey, visible]);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="80vw"
      centered
      closable={false}
      mask={true}
      maskStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(3px)',
      }}
      maskClosable={false}
      style={{
        maxWidth: '900px',
        background: 'transparent',
        boxShadow: 'none',
      }}
      bodyStyle={{
        padding: '60px 0 40px 0',
        height: '70vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
      }}
      className="creative-preview-modal"
      destroyOnClose={true}
    >
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '50px',
          zIndex: 10,
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '600',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          letterSpacing: '0.5px',
          maxWidth: '300px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {videoName || 'Video Unit'}
      </div>

      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '10px',
          right: '50px',
          zIndex: 10,
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
          color: 'white',
          width: '32px',
          height: '32px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        ×
      </div>

      <div
        style={{
          height: 'calc(70vh - 100px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 60px',
        }}
      >
        {videoUrl && (
          <video
            controls
            autoPlay
            width="100%"
            height="100%"
            key={videoUrl}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: '8px',
              boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
              objectFit: 'contain',
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    </Modal>
  );
};

const CreativeTable = ({ creativeData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideoKey, setSelectedVideoKey] = useState(null);
  const [selectedVideoName, setSelectedVideoName] = useState(null);

  const truncateUrl = (url, maxLength = 30) => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  const isVideoType = (record) => {
    // if (record.media && record.media.type) {
    //   return record.media.type.toLowerCase() === 'video' || record.media.type === 'THREEDEE';
    // }
    // // Fallback checker - check if creativeUrl contains video file extensions
    // if (record.creativeUrl) {
    //   const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogv', '.3gp', '.flv'];
    //   return videoExtensions.some((ext) => record.creativeUrl.toLowerCase().includes(ext));
    // }
    return false;
  };

  const isHTML5Type = (record) => {
    // if (record.media && record.media.type) {
    //   return record.media.type.toLowerCase() === 'html5' || record.media.type === 'HTML5';
    // }
    // // Fallback checker - check if creativeUrl contains html file extensions or is a zip
    // if (record.creativeUrl) {
    //   const html5Extensions = ['.html', '.htm', '.zip'];
    //   return html5Extensions.some((ext) => record.creativeUrl.toLowerCase().includes(ext));
    // }
    return false;
  };

  const extraColumn = {
    title: '',
    dataIndex: 'creativeUrl',
    key: 'creativeUrl',
    width: '70px',
    align: 'left',
    render: (url, record) => {
      const isVideo = isVideoType(record);
      const isHTML5 = isHTML5Type(record);
      const hasS3Video = false;

      // If we have an S3 video, show video preview
      if (hasS3Video) {
        return (
          <VideoPreviewCell
            s3VideoKey={record.s3VideoKey}
            onClick={() => {
              setSelectedVideoKey(record.s3VideoKey);
              setSelectedVideoName(record.value || 'Video Creative');
              setVideoModalVisible(true);
            }}
          />
        );
      }

      let iconComponent = <CloudOutlined />;
      if (isVideo) {
        iconComponent = <VideoCameraOutlined />;
      } else if (isHTML5) {
        iconComponent = <Html5Outlined />;
      }

      return (
        <Avatar
          shape="square"
          size={50}
          icon={iconComponent}
          src={url === '' || !url ? false : url}
          style={{ marginLeft: '-16px' }}
        />
      );
    },
  };

  const columns = [
    {
      title: '',
      dataIndex: 'color',
      key: 'color',
      width: '15px',
      render: (text) => {
        return (
          <Row
            type="flex"
            justify="center"
            align="middle"
            style={{
              width: '100%',
            }}
          >
            <div
              style={{
                backgroundColor: text,
                height: '15px',
                width: '15px',
                minWidth: '15px',
                borderRadius: '50px',
                marginLeft: '-16px',
              }}
            />
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Name'} />,
      dataIndex: 'value',
      key: 'value',
      width: '250px',
      align: 'left',
      footerContent: 'Totals + Averages',
      sorter: (a, b) => a.value.localeCompare(b.value),
      render: (text, record) => {
        if (text) {
          return (
            <Row
              type="flex"
              justify="start"
              align="middle"
              style={{
                width: '100%',
              }}
            >
              <Col span={24}>
                {text.length > 45 ? (
                  <Tooltip title={<TooltipText text={text} />}>
                    <span>
                      <PrimaryTableRowText text={text.slice(0, 46).concat('...')} />
                    </span>
                  </Tooltip>
                ) : (
                  <PrimaryTableRowText text={text} />
                )}
              </Col>
            </Row>
          );
        } else {
          return <div />;
        }
      },
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'impressions',
      footerSum: true,
      key: 'impressions',
      width: '130px',
      align: 'center',
      sorter: (a, b) => a.impressions - b.impressions,
      render: (impressions) => (
        <PrimaryTableRowText text={impressions && formatWholeNumber(impressions)} />
      ),
    },
    {
      title: <TableColumnTitle text={'Clicks'} />,
      footerSum: true,
      dataIndex: 'clicks',
      key: 'clicks',
      width: '100px',
      align: 'center',
      sorter: (a, b) => a.clicks - b.clicks,
      render: (clicks) => <PrimaryTableRowText text={clicks && formatWholeNumber(clicks)} />,
    },
    {
      title: <TableColumnTitle text={'CTR'} />,
      footerContent: '-',
      dataIndex: 'ctr',
      key: 'ctr',
      width: '80px',
      align: 'center',
      sorter: (a, b) => a.ctr - b.ctr,
      render: (ctr) => <PrimaryTableRowText text={ctr && `${ctr.toFixed(3)}%`} />,
    },
    {
      title: <TableColumnTitle text={'Click Through Link'} />,
      footerContent: '-',
      dataIndex: 'targetUrl',
      key: 'targetUrl',
      width: '200px',
      align: 'left',
      render: (targetUrl, record) => {
        if (!targetUrl) {
          return <PrimaryTableRowText text={'-'} />;
        }

        return (
          <Tooltip title={targetUrl}>
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => window.open(targetUrl, '_blank', 'noopener,noreferrer')}
              style={{
                height: 'auto',
                fontSize: '12px',
                color: '#1890ff',
              }}
            >
              {truncateUrl(targetUrl, 25)}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  if (creativeData && !isEmpty(creativeData)) {
    if (creativeData[0] && creativeData[0].creativeUrl) {
      columns.unshift(extraColumn);
    }
  }

  return (
    <>
      <SimpleTable
        footer={creativeData.slice(currentPage * 10 - 10, currentPage * 10)}
        bordered={false}
        columns={columns}
        data={creativeData.map((creative) => {
          return {
            ...creative,
            key: `${creative.id}${uuid.v4()}`,
          };
        })}
        pagination={{
          pageSize,
          total: creativeData && creativeData.length,
          current: currentPage,
        }}
        scroll={{ x: 200, y: 400 }}
        onChange={(paginationObject) => {
          setPageSize(paginationObject.pageSize);
          setCurrentPage(paginationObject.current);
        }}
        expandable={{
          expandedRowRender: (record) => {
            return record && <CreativeExpandedCard record={record} />;
          },
          rowExpandable: (record) => {
            const canExpand =
              (record.creativeUrl && !isVideoType(record) && !isHTML5Type(record)) ||
              // record.s3VideoKey ||
              (record.media && record.media.key);
            return canExpand;
          },
        }}
        rowClassName={(record) => {
          return record.creativeUrl
            ? // ||
              //   isVideoType(record) ||
              //   isHTML5Type(record) ||
              //   record.s3VideoKey
              ''
            : 'hide-expand';
        }}
      />

      <VideoModal
        s3VideoKey={selectedVideoKey}
        videoName={selectedVideoName}
        visible={videoModalVisible}
        onClose={() => setVideoModalVisible(false)}
      />
    </>
  );
};

export default CreativeTable;
